import { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, increment, limit, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../services/firebase';

export function useDevBoard() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'dev_board_messages'), orderBy('createdAt', 'desc'), limit(50));
    
    const unsubscribeDb = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(fetchedMessages);
      setLoading(false);
    });

    const leaderboardQuery = query(collection(db, 'users'), orderBy('devItCreds', 'desc'), limit(50));
    const unsubscribeLeaderboard = onSnapshot(leaderboardQuery, (snapshot) => {
      const fetchedUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeaderboard(fetchedUsers);
      setLeaderboardLoading(false);
    });

    return () => {
      unsubscribeDb();
      unsubscribeLeaderboard();
    };
  }, []);

  const subscribeToResponses = (messageId, callback) => {
    const q = query(collection(db, 'dev_board_messages', messageId, 'responses'), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const fetchedResponses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(fetchedResponses);
    });
  };

  const postMessage = async (content, isGuide = false, topic = 'General', aiModel = 'None', file = null) => {
    if (!auth.currentUser) throw new Error("Must be logged in");

    let attachmentUrl = null;
    let attachmentType = null;
    let attachmentName = null;

    if (file) {
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 800,
            useWebWorker: true,
            fileType: 'image/webp'
          };
          fileToUpload = await imageCompression(file, options);
          // Overwrite name to have .webp extension if successfully converted
          if (fileToUpload.type === 'image/webp' && !fileToUpload.name.endsWith('.webp')) {
             Object.defineProperty(fileToUpload, 'name', {
               writable: true,
               value: fileToUpload.name.replace(/\.[^/.]+$/, "") + ".webp"
             });
          }
        } catch (error) {
          console.error("Image compression failed:", error);
        }
      }

      const fileRef = ref(storage, `dev_board_attachments/${auth.currentUser.uid}/${Date.now()}_${fileToUpload.name}`);
      const snapshot = await uploadBytes(fileRef, fileToUpload);
      attachmentUrl = await getDownloadURL(snapshot.ref);
      attachmentType = fileToUpload.type;
      attachmentName = fileToUpload.name;
    }

    const docRef = await addDoc(collection(db, 'dev_board_messages'), {
      content,
      isGuide,
      isValidatedSolution: false,
      topic,
      aiModel,
      userId: auth.currentUser.uid,
      authorName: auth.currentUser.displayName || auth.currentUser.email,
      authorPhoto: auth.currentUser.photoURL,
      upvotes: 0,
      createdAt: new Date().toISOString(),
      isLocked: false,
      attachmentUrl,
      attachmentType,
      attachmentName
    });
    return docRef.id;
  };

  const postResponse = async (messageId, content) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    const docRef = await addDoc(collection(db, 'dev_board_messages', messageId, 'responses'), {
      content,
      userId: auth.currentUser.uid,
      authorName: auth.currentUser.displayName || auth.currentUser.email,
      authorPhoto: auth.currentUser.photoURL,
      createdAt: new Date().toISOString(),
      isVerified: false
    });
    return docRef.id;
  };

  const verifyResponse = async (messageId, responseId, responseAuthorId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    
    // Mark response as verified
    const responseRef = doc(db, 'dev_board_messages', messageId, 'responses', responseId);
    await updateDoc(responseRef, { isVerified: true });

    // Lock the parent message
    const messageRef = doc(db, 'dev_board_messages', messageId);
    await updateDoc(messageRef, { isLocked: true });

    // Award +5 points and update the timestamp
    await updateDoc(doc(db, 'users', responseAuthorId), {
      devItCreds: increment(5),
      lastCredsUpdate: serverTimestamp()
    });

    // Log the user action
    await addDoc(collection(db, 'user_activity_logs'), {
      action: 'VERIFY_RESPONSE',
      details: `User ${auth.currentUser.uid} verified response ${responseId} and awarded 5 creds to ${responseAuthorId}`,
      userId: auth.currentUser.uid,
      userName: auth.currentUser.displayName || auth.currentUser.email,
      targetUserId: responseAuthorId,
      credsAwarded: 5,
      timestamp: serverTimestamp()
    });
  };

  const validateSolution = async (messageId, authorId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const docRef = doc(db, 'dev_board_messages', messageId);
    
    await updateDoc(docRef, { isValidatedSolution: true, isLocked: true });

    // Award the author +10 DevIT Creds for a guide
    await updateDoc(doc(db, 'users', authorId), {
      devItCreds: increment(10),
      lastCredsUpdate: serverTimestamp()
    });

    // Log the user action
    await addDoc(collection(db, 'user_activity_logs'), {
      action: 'VALIDATE_SOLUTION',
      details: `User ${auth.currentUser.uid} validated guide ${messageId} and awarded 10 creds to ${authorId}`,
      userId: auth.currentUser.uid,
      userName: auth.currentUser.displayName || auth.currentUser.email,
      targetUserId: authorId,
      credsAwarded: 10,
      timestamp: serverTimestamp()
    });
  };

  const editMessage = async (messageId, updates) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const docRef = doc(db, 'dev_board_messages', messageId);
    await updateDoc(docRef, updates);
  };

  const deleteMessage = async (messageId) => {
    if (!auth.currentUser) throw new Error("Must be logged in");
    const docRef = doc(db, 'dev_board_messages', messageId);
    await deleteDoc(docRef);
  };

  return { 
    messages, 
    loading, 
    leaderboard,
    leaderboardLoading,
    postMessage, 
    validateSolution, 
    subscribeToResponses, 
    postResponse, 
    verifyResponse,
    editMessage,
    deleteMessage
  };
}
